import { useEffect } from 'react'
import {
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Button,
} from '@mui/material'
import { CollapsibleSection } from 'components/CollapsibleSection'
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useCreateTechnicalSubmissionMutation } from 'services/submissions/technical-submissions'
import { ITechnicalRequirement } from 'types/lots'
import { ISubmission } from 'types/submissions'
import {
  mapToTechnicalSubmissionDefaultValues,
  mapTechnicalSubmissionToPayload,
  TechnicalSubmissionRequirement,
} from './map-to-form-values'
import { FileUpload } from 'components/FileUpload'
import { useNavigate } from 'react-router-dom'

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
}))

type FieldValues = {
  technicalRequirement: TechnicalSubmissionRequirement
}

type Props = {
  technicalRequirement: ITechnicalRequirement
  submission: ISubmission
  hasDocDeadlinePassed: boolean
}

export const TechnicalSubmission = ({
  technicalRequirement,
  submission,
  hasDocDeadlinePassed,
}: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [createTechnicalSubmission, { isSuccess, isError, isUninitialized }] =
    useCreateTechnicalSubmissionMutation()

  useEffect(() => {
    if (isSuccess) {
      toast.success(t('operation-success'))
    }
    if (isError) {
      toast.error(t('failedToCreate'))
    }
  }, [isSuccess, isError, t])

  const methods = useForm({
    defaultValues: {
      technicalRequirement: mapToTechnicalSubmissionDefaultValues({
        technicalRequirement,
        technicalSubmission: submission.technicalSubmission,
      }),
    },
  })

  const { control, handleSubmit, watch } = methods

  const technicalReq = watch('technicalRequirement')
  const isItemsValid = technicalReq.technicalRequirementItems?.every(
    (item) => item.note && item.reply
  )

  const { fields } = useFieldArray({
    control,
    name: `technicalRequirement.technicalRequirementItems`,
  })

  const onSubmit = ({ technicalRequirement }: FieldValues) => {
    if (!isItemsValid) {
      toast.warning(t('fill-required-fields'))
      return
    }

    const body = mapTechnicalSubmissionToPayload(technicalRequirement, submission)

    createTechnicalSubmission(body)
  }

  if (!fields?.length) {
    return null
  }

  return (
    <Stack>
      <FormProvider {...methods}>
        <form>
          <CollapsibleSection title={t('technical-proposal')}>
            <TableContainer>
              <Table
                sx={{
                  tableLayout: 'fixed',
                  '& td, th': {
                    border: '1px solid #ccc',
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <StyledTableCell>{t('evaluationOfDocuments')}</StyledTableCell>
                    <StyledTableCell>{t('contractorOfferAndNotes')}</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fields.map((item, index) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <TechnicalSubmissionLeftColumn index={index} />
                      </TableCell>
                      <TableCell>
                        <TechnicalSubmissionRightColumn
                          index={index}
                          hasDocDeadlinePassed={hasDocDeadlinePassed}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CollapsibleSection>
          <Stack direction="row" justifyContent="flex-end">
            <Button
              disabled={
                !isItemsValid || (!methods.formState.isDirty && isUninitialized)
              }
              size="small"
              variant="contained"
              style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
              onClick={handleSubmit(onSubmit)}
            >
              {t('save')}
            </Button>
          </Stack>
        </form>
      </FormProvider>
    </Stack>
  )
}

const TechnicalSubmissionLeftColumn = ({ index }: { index: number }) => {
  const { t } = useTranslation()

  const { control } = useFormContext()

  return (
    <Stack spacing={1.5}>
      <Typography>{`${t('requirement')}: ${index + 1}`}</Typography>
      <Controller
        control={control}
        name={`technicalRequirement.technicalRequirementItems.${index}.criteria`}
        render={({ field }) => (
          <TextField
            {...field}
            InputProps={{
              rows: 2,
              multiline: true,
              inputComponent: 'textarea',
            }}
            fullWidth
            label={t('requirements')}
            disabled
            variant="outlined"
          />
        )}
      />
      <Controller
        control={control}
        name={`technicalRequirement.technicalRequirementItems.${index}.point`}
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            label={t('criterionValue')}
            disabled
            variant="outlined"
          />
        )}
      />
      <Controller
        control={control}
        name={`technicalRequirement.technicalRequirementItems.${index}.eligibility`}
        render={({ field }) => (
          <TextField
            {...field}
            InputProps={{
              rows: 2,
              multiline: true,
              inputComponent: 'textarea',
            }}
            fullWidth
            label={t('evaluationMethod')}
            disabled
            variant="outlined"
          />
        )}
      />
    </Stack>
  )
}

const TechnicalSubmissionRightColumn = ({
  index,
  hasDocDeadlinePassed,
}: {
  index: number
  hasDocDeadlinePassed: boolean
}) => {
  const { t } = useTranslation()

  const { control } = useFormContext()

  return (
    <Stack spacing={1.5}>
      <Typography>{`${t('answer')}: ${index + 1}`}</Typography>
      <Controller
        control={control}
        name={`technicalRequirement.technicalRequirementItems.${index}.reply`}
        render={({ field }) => (
          <TextField
            {...field}
            InputProps={{
              rows: 2,
              multiline: true,
              inputComponent: 'textarea',
            }}
            fullWidth
            label={`${t('answer')}: ${index + 1}`}
            required
            variant="outlined"
            disabled={hasDocDeadlinePassed}
          />
        )}
      />
      <Controller
        control={control}
        name={`technicalRequirement.technicalRequirementItems.${index}.note`}
        render={({ field }) => (
          <TextField
            {...field}
            InputProps={{
              rows: 2,
              multiline: true,
              inputComponent: 'textarea',
            }}
            fullWidth
            label={t('notes')}
            required
            variant="outlined"
            disabled={hasDocDeadlinePassed}
          />
        )}
      />

      <Controller
        name={`technicalRequirement.technicalRequirementItems.${index}.submittedDocumentFileId`}
        control={control}
        render={({ field }) => (
          <FileUpload
            text={t('document')}
            handleFile={(file) => field.onChange(file.id)}
            fileId={field.value}
            isDisabled={hasDocDeadlinePassed}
          />
        )}
      />
    </Stack>
  )
}
